import { z } from "zod";

const whatsappErrorSchema = z
  .object({
    code: z.number().optional(),
    title: z.string().optional(),
    message: z.string().optional(),
    error_data: z.object({ details: z.string().optional() }).passthrough().optional(),
  })
  .passthrough();

const whatsappMediaSchema = z
  .object({
    id: z.string().min(1),
    mime_type: z.string().optional(),
    sha256: z.string().optional(),
    filename: z.string().optional(),
    caption: z.string().optional(),
  })
  .passthrough();

export const metaWebhookEnvelopeSchema: z.ZodTypeAny = z
  .object({
    object: z.string(),
    entry: z.array(
      z
        .object({
          id: z.string(),
          time: z.number().optional(),
          /** Messenger and Instagram Direct: each event is parsed on its own (ADR 0092). */
          messaging: z.array(z.unknown()).optional(),
          changes: z
            .array(
              z
                .object({
                  field: z.string(),
                  value: z
                    .object({
                      messaging_product: z.string().optional(),
                      metadata: z
                        .object({
                          display_phone_number: z.string().optional(),
                          phone_number_id: z.string().optional(),
                        })
                        .passthrough()
                        .optional(),
                      contacts: z
                        .array(
                          z
                            .object({
                              wa_id: z.string(),
                              profile: z
                                .object({ name: z.string().optional() })
                                .passthrough()
                                .optional(),
                            })
                            .passthrough(),
                        )
                        .optional(),
                      messages: z
                        .array(
                          z
                            .object({
                              from: z.string(),
                              id: z.string(),
                              timestamp: z.string(),
                              type: z.string(),
                              text: z.object({ body: z.string() }).optional(),
                              image: whatsappMediaSchema.optional(),
                              audio: whatsappMediaSchema.optional(),
                              video: whatsappMediaSchema.optional(),
                              document: whatsappMediaSchema.optional(),
                              sticker: whatsappMediaSchema.optional(),
                              reaction: z
                                .object({ message_id: z.string(), emoji: z.string().nullable() })
                                .optional(),
                              location: z
                                .object({
                                  latitude: z.number(),
                                  longitude: z.number(),
                                  name: z.string().optional(),
                                  address: z.string().optional(),
                                })
                                .optional(),
                            })
                            .passthrough(),
                        )
                        .optional(),
                      statuses: z
                        .array(
                          z
                            .object({
                              id: z.string(),
                              status: z.string(),
                              timestamp: z.string(),
                              recipient_id: z.string().optional(),
                              errors: z.array(whatsappErrorSchema).optional(),
                            })
                            .passthrough(),
                        )
                        .optional(),
                      leadgen_id: z.string().optional(),
                      page_id: z.string().optional(),
                      form_id: z.string().optional(),
                      ad_id: z.string().optional(),
                    })
                    .passthrough(),
                })
                .passthrough(),
            )
            .optional(),
        })
        .passthrough(),
    ),
  })
  .passthrough();

export type MetaWebhookEnvelope = {
  object: string;
  entry: Array<{
    id: string;
    time?: number;
    messaging?: unknown[];
    changes?: Array<{
      field: string;
      value: Record<string, unknown>;
    }>;
  }>;
};

export const embeddedSignupExchangeSchema = z
  .object({
    code: z.string().min(8).max(4_096),
    state: z.string().min(32).max(1_024),
    businessId: z.string().min(1).max(128).optional(),
    wabaId: z.string().min(1).max(128),
    phoneNumberId: z.string().min(1).max(128).optional(),
  })
  .strict();

export const metaLeadAdsByoInputSchema = z
  .object({
    name: z.string().trim().min(1).max(160),
    appId: z.string().trim().min(1).max(128),
    appSecret: z.string().trim().min(16).max(4_096),
    accessToken: z.string().trim().min(16).max(8_192),
    pageId: z.string().trim().min(1).max(128),
    // With `allForms` every form of the Page is imported, including forms
    // created later. When omitted it follows `formId`: no form means all forms.
    allForms: z.boolean().optional(),
    formId: z.string().trim().max(128).default(""),
  })
  .strict()
  .transform((input) => ({ ...input, allForms: input.allForms ?? input.formId.length === 0 }))
  .superRefine((input, context) => {
    if (!input.allForms && input.formId.length === 0) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["formId"],
        message: "Indica il modulo oppure importa tutti i moduli della Pagina.",
      });
    }
  });

export const metaLeadAdsSettingsInputSchema = z
  .object({ allForms: z.boolean(), version: z.number().int().positive() })
  .strict();

// A WhatsApp connection fed by the operator's WhatsApp Desktop database while
// Coexistence is not available. The Embedded Signup later reuses the same
// connection and closes the local import at `cutoffAt`.
export const LOCAL_IMPORT_SOURCE = "whatsapp_desktop_mac";

export const localImportMetadataSchema = z
  .object({
    enabled: z.boolean(),
    source: z.literal(LOCAL_IMPORT_SOURCE),
    createdAt: z.string().datetime({ offset: true }),
    cutoffAt: z.string().datetime({ offset: true }).nullable(),
    lastImportAt: z.string().datetime({ offset: true }).nullable().default(null),
  })
  .passthrough();
export type LocalImportMetadata = z.infer<typeof localImportMetadataSchema>;

export function readLocalImport(metadata: unknown): LocalImportMetadata | null {
  if (typeof metadata !== "object" || metadata === null || !("localImport" in metadata)) {
    return null;
  }
  const parsed = localImportMetadataSchema.safeParse(metadata.localImport);
  return parsed.success ? parsed.data : null;
}
